import { zodResolver } from '@hookform/resolvers/zod'
// Multipart FormData uploads need a custom `bodySerializer`, which `$api.useMutation`
// does not expose.
import { useMutation } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'
import { extractErrorMessage } from '@joyful/utils'

import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Fieldset,
  Form,
  FormTextareaField,
  Stack,
  toast,
} from '../../../../core'
import { useApiQueryClient } from '../../../utils/reactQuery'
import { useCurrentUser } from '../../../utils/currentUserContext'
import { useSafeApiOrgSlug } from '../../../utils/orgSlug'
import type { UserFeedbackDialogController } from '../hooks/useUserFeedbackDialog'
import { stripDialogQueryParams } from '../hooks/useUserFeedbackDialog'
import {
  handleScreenshotAttachmentPaste,
  ScreenshotAttachmentField,
} from './ScreenshotAttachmentField'

const ChangeProposalScreenshotFileName = 'change-proposal-screenshot.png'

const ProposeChangeFormSchema = z.object({
  changeRequest: z.string().trim().min(1, `A change request is required`),
})

type ProposeChangeFormValues = z.infer<typeof ProposeChangeFormSchema>

export interface ProposeChangeDialogProps {
  proposeChangeDialog: UserFeedbackDialogController
}

export function ProposeChangeDialog({
  proposeChangeDialog,
}: ProposeChangeDialogProps) {
  const activeOrgSlug = useSafeApiOrgSlug()
  const currentUser = useCurrentUser()
  const apiOrgSlug = activeOrgSlug ?? currentUser.accounts.at(0)?.slug ?? null

  if (apiOrgSlug === null) {
    return null
  }

  return (
    <ProposeChangeDialogContent
      apiOrgSlug={apiOrgSlug}
      proposeChangeDialog={proposeChangeDialog}
    />
  )
}

function ProposeChangeDialogContent({
  apiOrgSlug,
  proposeChangeDialog,
}: ProposeChangeDialogProps & { apiOrgSlug: string }) {
  const { fetchClient } = useApiQueryClient()
  const form = useForm<ProposeChangeFormValues>({
    defaultValues: {
      changeRequest: '',
    },
    resolver: zodResolver(ProposeChangeFormSchema),
  })

  const proposeChangeMutation = useMutation({
    mutationFn: async (values: ProposeChangeFormValues) => {
      const pageUrl = stripDialogQueryParams(
        proposeChangeDialog.screenshot?.pageUrl ?? window.location.href,
      )
      const formData = new FormData()
      formData.append('change_request', values.changeRequest)
      formData.append('page_url', pageUrl)
      if (proposeChangeDialog.screenshot !== null) {
        const screenshotFile = new File(
          [proposeChangeDialog.screenshot.blob],
          ChangeProposalScreenshotFileName,
          {
            type: proposeChangeDialog.screenshot.blob.type || 'image/png',
          },
        )
        formData.append('screenshot', screenshotFile)
      }

      const result = await fetchClient.POST('/{org_slug}/propose-change', {
        body: {
          change_request: values.changeRequest,
          page_url: pageUrl,
          screenshot: proposeChangeDialog.screenshot === null ? undefined : '',
        },
        bodySerializer: () => {
          return formData
        },
        params: {
          path: {
            org_slug: apiOrgSlug,
          },
        },
      })
      if (result.error) {
        throw new Error(
          extractErrorMessage(result.error) ??
            'The change could not be proposed.',
        )
      }

      if (result.data === undefined) {
        throw new Error('The change could not be proposed.')
      }

      return result.data
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error))
    },
    onSuccess: (result) => {
      toast.success(`Change proposed`, {
        action: (
          <Button asChild size="xs" variant="outline">
            <a href={result.agent_url} rel="noreferrer" target="_blank">
              {`Open agent`}
            </a>
          </Button>
        ),
      })
      form.reset()
      proposeChangeDialog.closeFeedbackDialog()
    },
  })

  return (
    <Dialog
      open={proposeChangeDialog.isDialogOpen}
      onOpenChange={(nextOpen) => {
        if (proposeChangeMutation.isPending) {
          return
        }

        if (nextOpen === false) {
          proposeChangeDialog.closeFeedbackDialog()
        }
      }}
    >
      <DialogContent asChild className="z-50 max-w-2xl">
        <Form
          shouldSubmitWithHotkey
          onPaste={(event) => {
            handleScreenshotAttachmentPaste({
              event,
              isDisabled: proposeChangeMutation.isPending,
              screenshotDialog: proposeChangeDialog,
            })
          }}
          onSubmit={form.handleSubmit((values) => {
            proposeChangeMutation.mutate(values)
          })}
        >
          <FormProvider {...form}>
            <DialogHeader hasDivider>
              <DialogTitle>{`Propose a change`}</DialogTitle>
              <DialogDescription>
                {`Describe the change. A cloud agent will implement it.`}
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <Fieldset disabled={proposeChangeMutation.isPending}>
                <Stack gap="md">
                  <ScreenshotAttachmentField
                    isDisabled={proposeChangeMutation.isPending}
                    screenshotDialog={proposeChangeDialog}
                  />
                  <FormTextareaField
                    control={form.control}
                    name="changeRequest"
                    label={`Change`}
                    isRequired
                    orientation="vertical"
                    textareaProps={{
                      placeholder: `Describe the change you want...`,
                      rows: 5,
                    }}
                  />
                </Stack>
              </Fieldset>
            </DialogBody>

            <DialogFooter hasDivider>
              <DialogClose asChild>
                <Button
                  variant="outline"
                  disabled={proposeChangeMutation.isPending}
                >
                  {`Cancel`}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                variant="brand-subtle"
                isPending={proposeChangeMutation.isPending}
              >
                {`Propose change`}
              </Button>
            </DialogFooter>
          </FormProvider>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
